import logger from './logger';

/**
 * CORS Proxy Solution
 * Handles CORS issues when calling backend APIs from frontend
 */

const API_BASE_URL = process.env.NODE_ENV === 'development'
  ? 'http://localhost:5000/api/v1/customer/profile'
  : 'https://api.visibeen.com/api/v1/customer/profile';
const API_KEY = process.env.REACT_APP_GOOGLE_API_KEY || process.env.REACT_APP_GOOGLE_PLACES_API_KEY;

/**
 * Make API calls with CORS handling
 * @param {string} endpoint - API endpoint path
 * @param {Object} params - Query parameters
 * @param {Object} options - Fetch options
 * @returns {Promise} API response
 */
export async function corsApiCall(endpoint, params = {}, options = {}) {
  logger.log('🌐 [CORS] Making API call to:', endpoint);
  logger.log('🌐 [CORS] Parameters:', params);

  // Method 1: Try direct call first (in case CORS is fixed)
  try {
    const result = await directApiCall(endpoint, params, options);
    logger.log('✅ [CORS] Direct call succeeded');
    return result;
  } catch (error) {
    logger.warn('❌ [CORS] Direct call failed:', error.message);

    // Method 2: Try with different CORS modes
    try {
      const result = await corsWorkaroundCall(endpoint, params, options);
      logger.log('✅ [CORS] Workaround call succeeded');
      return result;
    } catch (corsError) {
      logger.error('❌ [CORS] All methods failed:', corsError.message);
      // Don't throw - return null to handle gracefully
      return null;
    }
  }
}

/**
 * Direct API call (normal method)
 */
async function directApiCall(endpoint, params = {}, options = {}) {
  const url = new URL(`${API_BASE_URL}${endpoint}`);

  // Add query parameters
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      url.searchParams.set(key, value.toString());
    }
  });

  // Add API key if available
  if (API_KEY) {
    url.searchParams.set('key', API_KEY);
  }

  const headers = {
    'Content-Type': 'application/json',
    ...options.headers
  };

  // Add authentication headers
  if (API_KEY) {
    headers['X-API-Key'] = API_KEY;
    headers['Authorization'] = `Bearer ${API_KEY}`;
    headers['X-Google-API-Key'] = API_KEY;
  }

  const response = await fetch(url.toString(), {
    method: options.method || 'GET',
    headers: headers,
    mode: 'cors',
    credentials: 'omit',
    ...options
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`API Error: ${response.status} ${response.statusText} - ${errorText}`);
  }

  return await response.json();
}

/**
 * CORS workaround call with different modes
 */
async function corsWorkaroundCall(endpoint, params = {}, options = {}) {
  const url = new URL(`${API_BASE_URL}${endpoint}`);

  // Add query parameters
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      url.searchParams.set(key, value.toString());
    }
  });

  // Add API key if available
  if (API_KEY) {
    url.searchParams.set('key', API_KEY);
  }

  // Try with no-cors mode (limited response access)
  try {
    const response = await fetch(url.toString(), {
      method: options.method || 'GET',
      mode: 'no-cors',
      credentials: 'omit'
    });

    // Note: no-cors mode doesn't allow reading response body
    // This is just to test if the request goes through
    logger.log('🌐 [CORS] no-cors request sent, but cannot read response');
    throw new Error('no-cors mode cannot read response');
  } catch (error) {
    // Try with a CORS proxy service (for development only)
    return await proxyApiCall(endpoint, params, options);
  }
}

/**
 * Use a public CORS proxy (for development only - not recommended for production)
 */
async function proxyApiCall(endpoint, params = {}, options = {}) {
  logger.warn('🌐 [CORS] Using CORS proxy - only for development!');

  const originalUrl = new URL(`${API_BASE_URL}${endpoint}`);

  // Add query parameters
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      originalUrl.searchParams.set(key, value.toString());
    }
  });

  // Add API key if available
  if (API_KEY) {
    originalUrl.searchParams.set('key', API_KEY);
  }

  // Use a public CORS proxy (there are several available)
  const proxyUrls = [
    `https://cors-anywhere.herokuapp.com/${originalUrl.toString()}`,
    `https://api.allorigins.win/get?url=${encodeURIComponent(originalUrl.toString())}`,
    `https://corsproxy.io/?${encodeURIComponent(originalUrl.toString())}`
  ];

  for (const proxyUrl of proxyUrls) {
    try {
      logger.log('🌐 [CORS] Trying proxy:', proxyUrl);

      const response = await fetch(proxyUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Proxy failed: ${response.status}`);
      }

      const data = await response.json();

      // Handle different proxy response formats
      if (data.contents) {
        // allorigins format
        return JSON.parse(data.contents);
      } else if (data.response) {
        // Some proxies wrap in response
        return data.response;
      } else {
        // Direct response
        return data;
      }
    } catch (error) {
      logger.warn(`🌐 [CORS] Proxy failed: ${proxyUrl}`, error.message);
      continue;
    }
  }

  throw new Error('All CORS proxy methods failed');
}

/**
 * Test CORS configuration
 */
export async function testCorsConfiguration() {
  logger.log('🌐 [CORS] Testing CORS configuration...');

  const testEndpoint = '/textsearch';
  const testParams = { query: 'test' };

  try {
    const result = await corsApiCall(testEndpoint, testParams);
    logger.log('✅ [CORS] CORS test successful:', result);
    return { success: true, data: result };
  } catch (error) {
    logger.error('❌ [CORS] CORS test failed:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Get backend CORS configuration instructions
 */
export function getCorsInstructions() {
  return {
    problem: "CORS (Cross-Origin Resource Sharing) is blocking frontend requests to your backend API",
    solution: "Configure your backend server to allow requests from your frontend domain",
    instructions: {
      "Node.js/Express": {
        install: "npm install cors",
        code: `
const cors = require('cors');

// Allow all origins (development only)
app.use(cors());

// Or allow specific origins (recommended for production)
app.use(cors({
  origin: ['http://localhost:3000', 'https://yourdomain.com'],
  credentials: true,
  methods: ['GET', 'POST', 'PUT', 'DELETE', 'OPTIONS'],
  allowedHeaders: ['Content-Type', 'Authorization', 'X-API-Key', 'X-Google-API-Key']
}));`
      },
      "Python/Flask": {
        install: "pip install flask-cors",
        code: `
from flask_cors import CORS

app = Flask(__name__)
CORS(app, origins=['http://localhost:3000', 'https://yourdomain.com'])`
      },
      "Python/Django": {
        install: "pip install django-cors-headers",
        code: `
# settings.py
INSTALLED_APPS = [
    'corsheaders',
    # ... other apps
]

MIDDLEWARE = [
    'corsheaders.middleware.CorsMiddleware',
    # ... other middleware
]

CORS_ALLOWED_ORIGINS = [
    "http://localhost:3000",
    "https://yourdomain.com",
]`
      },
      "Java/Spring": {
        code: `
@CrossOrigin(origins = {"http://localhost:3000", "https://yourdomain.com"})
@RestController
public class ApiController {
    // Your API endpoints
}`
      }
    },
    quickFix: "Add these headers to your backend API responses:",
    headers: {
      "Access-Control-Allow-Origin": "http://localhost:3000",
      "Access-Control-Allow-Methods": "GET, POST, PUT, DELETE, OPTIONS",
      "Access-Control-Allow-Headers": "Content-Type, Authorization, X-API-Key, X-Google-API-Key",
      "Access-Control-Allow-Credentials": "true"
    }
  };
}

// Make functions available globally for console testing
if (typeof window !== 'undefined') {
  window.corsApiCall = corsApiCall;
  window.testCorsConfiguration = testCorsConfiguration;
  window.getCorsInstructions = getCorsInstructions;
}
